import * as React from 'react'
import { Box, Form, Typography } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar, FormikTextField, FormikSelect } from 'components'
import { Grid } from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  CustomerInterfaceConfigurationKey,
  ApiConnectorConfigurationRequestViewModel,
  WebAdminUser,
  SupportUserPermission,
} from '../../api'
import {
  useConnectorApi,
  usePageNotifications,
  useCurrentCustomer,
  useLogger,
  useAuth,
  useCustomerApi,
  useCustomers,
} from 'hooks'
import { CustomerInterface } from 'contexts/CurrentCustomerContext'
import { getCustomerInterfaceConfigurationKeysLabel } from 'utils'
import { Option } from 'cs-kevlar-react/lib/es/Select'

interface InterfaceConfigurationProps {
  connector: CustomerInterface
  onClose: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      margin: 0,
      marginTop: theme.spacing(8),
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
    gridItem: {
      width: '100%',
    },
    certStatus: {
      marginTop: theme.spacing(8),
    },
  }),
)

type ConfigurationValues = {
  [key: string]: string
}

const booleanOptions: Option[] = [
  {
    key: 'unset',
    label: 'Use connector value',
    value: '',
  },
  {
    key: 'true',
    label: 'true',
    value: 'true',
  },
  {
    key: 'false',
    label: 'false',
    value: 'false',
  },
]

const canManageInterfaceConfiguration = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(
      SupportUserPermission.ManageInterfaceConfiguration,
    )
  )
}

export const InterfaceConfiguration: React.FunctionComponent<InterfaceConfigurationProps> = ({
  connector,
  onClose,
}) => {
  const classes = useStyles({})
  const { logError } = useLogger()

  if (!connector.customerAgencyId) return null

  const { configureConnector } = useConnectorApi()
  const { getCustomerSummary } = useCustomerApi()
  const { user: authUser } = useAuth()

  const { dispatch: dispatchCustomerAction } = useCustomers()

  const canEditConfigs = canManageInterfaceConfiguration(authUser)

  const { dispatch } = useCurrentCustomer(connector.customerAgencyId)

  const { dispatch: dispatchNotification } = usePageNotifications()

  const onConfigure = async (
    values: ConfigurationValues,
    formikActions: any,
  ) => {
    if (!connector.customerAgencyId || !connector.customerInterfaceId) {
      formikActions.setSetSubmitting(false)
      return
    }

    let isValidForm = true
    if (
      values[
        CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()
      ]
    ) {
      if (
        values[
          CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()
        ] !== '' &&
        values[
          CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()
        ] !== 'true' &&
        values[
          CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()
        ] !== 'false'
      ) {
        formikActions.setFieldError(
          CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString(),
          'Must be an empty string, true, or false',
        )
        isValidForm = false
      }
    }

    if (
      values[
        CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()
      ]
    ) {
      if (
        values[
          CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()
        ] !== '' &&
        values[
          CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()
        ] !== 'true' &&
        values[
          CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()
        ] !== 'false'
      ) {
        formikActions.setFieldError(
          CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString(),
          'Must be an empty string, true, or false',
        )
        isValidForm = false
      }
    }

    if (!isValidForm) {
      formikActions.setSetSubmitting(false)
      return
    }

    const apiRequestDto: ApiConnectorConfigurationRequestViewModel = {
      overrideValues: {},
      overridesToRemove: [],
    }
    Object.keys(values).forEach((key) => {
      if (
        key ===
        CustomerInterfaceConfigurationKey.CustomerInterfaceLogging.toString()
      ) {
        // skip this.  they have to edit logging configs on a different screen
      } else if (values[key] === '') {
        apiRequestDto.overridesToRemove.push(key as any)
      } else {
        apiRequestDto.overrideValues[key] = values[key]
      }
    })

    // if no errors, make API call
    const response = await configureConnector(
      connector.customerAgencyId,
      connector.customerInterfaceId,
      apiRequestDto,
    )

    if (response.status === 200 && response.data.isSuccess) {
      dispatch({
        type: 'UpdateConnectorConfiguration',
        payload: {
          customerInterfaceId: connector.customerInterfaceId,
          configuration: response.data.data,
        },
      })

      try {
        const updatedCustomerResponse = await getCustomerSummary(
          connector.customerAgencyId,
        )

        if (
          updatedCustomerResponse.status === 200 &&
          updatedCustomerResponse.data
        )
          dispatchCustomerAction({
            type: 'UpdateCustomerSummary',
            payload: updatedCustomerResponse.data,
          })
      } catch (e) {
        logError(e, 'Failed to refresh the customer summary')
      }

      onClose()
    } else {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was a problem updating the interface configuration',
      })
    }

    formikActions.setSubmitting(false)
  }

  const configurationOverrides: ConfigurationValues = Object.keys(
    connector.configurationOverrides,
  )
    .filter(
      (key) =>
        key !==
        getCustomerInterfaceConfigurationKeysLabel(
          CustomerInterfaceConfigurationKey.CustomerInterfaceLogging,
        ),
    )
    .reduce((accum, key) => {
      const enumKey = CustomerInterfaceConfigurationKey[key as any]
      return {
        ...accum,
        [enumKey]: connector.configurationOverrides[key],
      }
    }, {})

  return (
    <React.Fragment>
      <Formik initialValues={configurationOverrides} onSubmit={onConfigure}>
        {(formikProps) => {
          return (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Typography>
                  Specify override values for the connector configuration.
                  Leaving a value blank will result in the connector's original
                  value getting re-applied.
                </Typography>
                <Grid
                  container
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                  className={classes.formFields}
                >
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.CommunicationNebulaResponseTimeoutInMilliSeconds.toString()}
                      label="Communication.NebulaResponseTimeoutInMilliSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.CommunicationMaxNebulaImportExecutionDelays.toString()}
                      label="Communication.MaxNebulaImportExecutionDelays"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikSelect
                      id={CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()}
                      formikProps={formikProps}
                      label="Communication.AllowNebulaToRequestMoreTime"
                      fieldName={CustomerInterfaceConfigurationKey.CommunicationAllowNebulaToRequestMoreTime.toString()}
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.DefaultDocumentSourceCacheCapacity.toString()}
                      label="DefaultDocumentSourceCacheCapacity"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.ExistenceCheckPeriodInSeconds.toString()}
                      label="ExistenceCheckPeriodInSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.ImportFailureCacheCapacity.toString()}
                      label="ImportFailureCacheCapacity"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.ImportFailureThreshold.toString()}
                      label="ImportFailureThreshold"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.InitialBackoffInSeconds.toString()}
                      label="InitialBackoffInSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.MaxBackoffInSeconds.toString()}
                      label="MaxBackoffInSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.MaxRecoverAttempts.toString()}
                      label="MaxRecoverAttempts"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.MorseLMaxRecordBatch.toString()}
                      label="MorseL.MaxRecordBatch"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.MorseLMaxRecordBatchByteSize.toString()}
                      label="MorseL.MaxRecordBatchByteSize"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikSelect
                      id={CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()}
                      formikProps={formikProps}
                      label="MorseL.SupportMaxBatchByteSize"
                      fieldName={CustomerInterfaceConfigurationKey.MorseLSupportMaxBatchByteSize.toString()}
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.MorseLTimeoutInMilliSeconds.toString()}
                      label="MorseL.TimeoutInMilliSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.PositionPushIntervalInSeconds.toString()}
                      label="PositionPushIntervalInSeconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={CustomerInterfaceConfigurationKey.PushIntervalInMilliseconds.toString()}
                      label="PushIntervalInMilliseconds"
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikSelect
                      id={CustomerInterfaceConfigurationKey.DangerouslyEnableDebugLoggingForPatching.toString()}
                      formikProps={formikProps}
                      label="DangerouslyEnableDebugLoggingForPatching"
                      fieldName={CustomerInterfaceConfigurationKey.DangerouslyEnableDebugLoggingForPatching.toString()}
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikSelect
                      id={CustomerInterfaceConfigurationKey.DisableDocumentPatching.toString()}
                      formikProps={formikProps}
                      label="DisableDocumentPatching"
                      fieldName={CustomerInterfaceConfigurationKey.DisableDocumentPatching.toString()}
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikSelect
                      id={CustomerInterfaceConfigurationKey.FileUploadEnabled.toString()}
                      formikProps={formikProps}
                      label="FileUploadEnabled"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadEnabled.toString()}
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadImageMaxSizeBytes.toString()}
                      formikProps={formikProps}
                      label="FileUploadImageMaxSizeBytes"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadImageMaxSizeBytes.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadAudioMaxSizeBytes.toString()}
                      formikProps={formikProps}
                      label="FileUploadAudioMaxSizeBytes"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadAudioMaxSizeBytes.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadVideoMaxSizeBytes.toString()}
                      formikProps={formikProps}
                      label="FileUploadVideoMaxSizeBytes"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadVideoMaxSizeBytes.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadDocumentMaxSizeBytes.toString()}
                      formikProps={formikProps}
                      label="FileUploadDocumentMaxSizeBytes"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadDocumentMaxSizeBytes.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadFailureThreshold.toString()}
                      formikProps={formikProps}
                      label="FileUploadFailureThreshold"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadFailureThreshold.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadQueueSecondsBetweenFailures.toString()}
                      formikProps={formikProps}
                      label="FileUploadQueueSecondsBetweenFailures"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadQueueSecondsBetweenFailures.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      id={CustomerInterfaceConfigurationKey.FileUploadWorkers.toString()}
                      formikProps={formikProps}
                      label="FileUploadWorkers"
                      fieldName={CustomerInterfaceConfigurationKey.FileUploadWorkers.toString()}
                      type="number"
                      fullWidth
                      disabled={!canEditConfigs}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box className={classes.spacer} />
              <EditModalActionBar
                canDelete={false}
                onCancel={onClose}
                canSubmit={canEditConfigs}
                formikProps={formikProps}
                saveText="Save"
              />
            </Form>
          );
        }}
      </Formik>
    </React.Fragment>
  );
}
